
import React from 'react';
import { 
  ThumbsUp, 
  ThumbsDown,
  User,
  Bot,
  Link as LinkIcon,
  Sparkles
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AIMessage } from '@/contexts/ai-assistant/types';
import { applyMarkdownFormatting } from '@/utils/textFormatting';

interface MessageItemProps {
  message: AIMessage;
  rateResponse: (messageId: string, rating: 'helpful' | 'not-helpful') => void;
  onSourceClick: (url: string) => void;
}

const MessageItem: React.FC<MessageItemProps> = ({
  message,
  rateResponse,
  onSourceClick
}) => {
  return (
    <div className={`flex ${message.role !== 'user' ? 'justify-start' : 'justify-end'}`}>
      <div 
        className={`relative max-w-[80%] px-4 py-3 rounded-lg ${
          message.role === 'user' 
            ? 'bg-primary text-primary-foreground ml-auto' 
            : message.role === 'system' 
              ? 'bg-muted text-foreground' 
              : 'bg-accent text-accent-foreground'
        }`}
      >
        <div className="flex items-center gap-2 mb-1 text-xs opacity-80">
          {message.role === 'user' ? (
            <User className="h-3 w-3" />
          ) : message.role === 'system' ? (
            <Bot className="h-3 w-3" />
          ) : (
            <Bot className="h-3 w-3" />
          )}
          {message.role === 'user' ? 'You' : message.role === 'system' ? 'System' : 'AI Assistant'}
          <span>·</span>
          <span>
            {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </span>
        </div>
        
        {message.isLoading ? (
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 animate-spin" />
            <span>Thinking...</span>
          </div>
        ) : (
          <div>
            <p 
              className="whitespace-pre-wrap break-words" 
              dangerouslySetInnerHTML={{ 
                __html: applyMarkdownFormatting(message.content) 
              }} 
            />
            
            {message.sources && message.sources.length > 0 && (
              <div className="mt-2 pt-2 border-t border-primary/10">
                <p className="text-xs flex items-center gap-1 mb-1 opacity-80">
                  <LinkIcon className="h-3 w-3" />
                  Sources:
                </p>
                <div className="flex flex-wrap gap-1">
                  {message.sources.map((source, index) => (
                    <Badge 
                      key={index} 
                      variant="outline" 
                      className="text-xs cursor-pointer hover:bg-muted"
                      onClick={() => onSourceClick(source.url)}
                    >
                      {source.title}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            
            {message.role === 'assistant' && !message.isLoading && (
              <div className="flex gap-2 mt-2 justify-end">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-6 px-1"
                  onClick={() => rateResponse(message.id, 'helpful')}
                >
                  <ThumbsUp className="h-3 w-3" />
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-6 px-1"
                  onClick={() => rateResponse(message.id, 'not-helpful')}
                >
                  <ThumbsDown className="h-3 w-3" />
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MessageItem;
